import type { CreateTaxonomyNodeRequest, ListTaxonomyNodesResponse } from '@/api/generatedApi';
import { api } from '@/services/api';
import { useTenants } from '@/services/tenants/useTenants';
import { getApiError } from '@/types/apiError';
import { InfiniteData, useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';
import { TAXONOMY_CHILDREN_QUERY_KEY } from './useGetTaxonomyChildren';

export const useCreateTaxonomyNode = (taxonomyId: string) => {
  const queryClient = useQueryClient();
  const { getTenantId } = useTenants();
  const tenantId = getTenantId();

  const { mutate, isPending, error } = useMutation({
    mutationFn: (data: CreateTaxonomyNodeRequest) => api.taxonomy.createTaxonomyNode(tenantId, taxonomyId, data),
    onSuccess: (response, variables) => {
      const newNode = response.data.node;
      const parentNodeId = variables.parentNodeId ?? 'root';
      const queryKey = [TAXONOMY_CHILDREN_QUERY_KEY, tenantId, taxonomyId, parentNodeId];

      queryClient.setQueryData<InfiniteData<AxiosResponse<ListTaxonomyNodesResponse>>>(queryKey, old => {
        if (!old?.pages?.[0]) return old;
        const firstPage = old.pages[0];
        return {
          ...old,
          pages: [
            {
              ...firstPage,
              data: {
                ...firstPage.data,
                nodes: [...firstPage.data.nodes, newNode],
                pageData: {
                  ...firstPage.data.pageData,
                  total: firstPage.data.pageData.total + 1,
                },
              },
            },
            ...old.pages.slice(1),
          ],
        };
      });

      if (variables.parentNodeId) {
        queryClient.setQueriesData<InfiniteData<AxiosResponse<ListTaxonomyNodesResponse>>>(
          { queryKey: [TAXONOMY_CHILDREN_QUERY_KEY] },
          old => {
            if (!old?.pages) return old;
            return {
              ...old,
              pages: old.pages.map(page => ({
                ...page,
                data: {
                  ...page.data,
                  nodes: page.data.nodes.map(n => (n.id === variables.parentNodeId ? { ...n, hasChildren: true } : n)),
                },
              })),
            };
          }
        );
      }

      queryClient.invalidateQueries({
        queryKey: [TAXONOMY_CHILDREN_QUERY_KEY],
        exact: false,
      });
    },
  });

  return {
    createNode: mutate,
    isCreating: isPending,
    createError: getApiError(error),
  };
};
